import React, { useState, useMemo } from 'react';
import { DollarSign, Truck, AlertTriangle, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface RefundComposerProps {
  orderId: string;
  items: OrderItem[];
  deliveryFee: number;
  totalPaid: number;
  onSubmit: (refundData: {
    itemRefunds: { itemId: string; amount: number }[];
    deliveryFeeRefund: number;
    reason: string;
  }) => void;
  onCancel: () => void;
}

export function RefundComposer({ 
  orderId, 
  items, 
  deliveryFee, 
  totalPaid, 
  onSubmit, 
  onCancel 
}: RefundComposerProps) {
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const [refundDelivery, setRefundDelivery] = useState(false);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleItem = (itemId: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const calculateRefund = useMemo(() => {
    let itemTotal = 0;
    items.forEach(item => {
      if (selectedItems[item.id]) {
        itemTotal += item.price * item.quantity;
      }
    });
    
    const deliveryRefund = refundDelivery ? deliveryFee : 0;
    return {
      items: itemTotal,
      delivery: deliveryRefund,
      total: itemTotal + deliveryRefund,
    };
  }, [selectedItems, refundDelivery, items, deliveryFee]);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    
    setIsSubmitting(true);
    
    const itemRefunds = items
      .filter(item => selectedItems[item.id])
      .map(item => ({
        itemId: item.id,
        amount: item.price * item.quantity,
      }));
    
    await onSubmit({
      itemRefunds,
      deliveryFeeRefund: refundDelivery ? deliveryFee : 0,
      reason,
    });
    
    setIsSubmitting(false);
  };

  const hasSelection = Object.values(selectedItems).some(Boolean) || refundDelivery;

  return (
    <Card className="max-w-2xl">
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="flex items-center space-x-2">
          <DollarSign className="h-5 w-5 text-primary" />
          <span>Compose Refund</span>
        </CardTitle>
        <p className="text-sm text-body">Order #{orderId.slice(-6)}</p>
      </CardHeader>
      
      <CardContent className="py-6 space-y-6">
        {/* Item Selection */}
        <div>
          <h4 className="font-medium text-heading mb-3">Select items to refund</h4>
          <div className="space-y-2">
            {items.map(item => (
              <button
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className={cn(
                  "w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all",
                  selectedItems[item.id]
                    ? "border-primary bg-primary/5"
                    : "border-gray-100 hover:border-gray-200"
                )}
              >
                <div className="flex items-center space-x-3">
                  <div className={cn(
                    "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors",
                    selectedItems[item.id]
                      ? "border-primary bg-primary"
                      : "border-gray-300"
                  )}>
                    {selectedItems[item.id] && <Check className="h-4 w-4 text-white" />}
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-heading">{item.name}</p>
                    <p className="text-sm text-body">Qty: {item.quantity}</p>
                  </div>
                </div>
                <p className="font-medium text-heading">
                  ${(item.price * item.quantity).toFixed(2)}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Delivery Fee Toggle */}
        <div>
          <button
            onClick={() => setRefundDelivery(!refundDelivery)}
            className={cn(
              "w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all",
              refundDelivery
                ? "border-primary bg-primary/5"
                : "border-gray-100 hover:border-gray-200"
            )}
          >
            <div className="flex items-center space-x-3">
              <div className={cn(
                "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors",
                refundDelivery
                  ? "border-primary bg-primary"
                  : "border-gray-300"
              )}>
                {refundDelivery && <Check className="h-4 w-4 text-white" />}
              </div>
              <div className="flex items-center space-x-2">
                <Truck className="h-5 w-5 text-body" />
                <span className="font-medium text-heading">Refund delivery fee</span>
              </div>
            </div>
            <p className="font-medium text-heading">${deliveryFee.toFixed(2)}</p>
          </button>
        </div>

        {/* Reason */}
        <div>
          <label className="block font-medium text-heading mb-2">
            Reason for refund <span className="text-error">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this refund is being issued..."
            rows={3}
            className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light resize-none"
          />
        </div>

        {/* Refund Preview */}
        {hasSelection && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-medium text-heading mb-3">Refund Summary</h4>
            <div className="space-y-2 text-sm">
              {calculateRefund.items > 0 && (
                <div className="flex justify-between">
                  <span className="text-body">Item refund</span>
                  <span className="text-heading">${calculateRefund.items.toFixed(2)}</span>
                </div>
              )}
              {calculateRefund.delivery > 0 && (
                <div className="flex justify-between">
                  <span className="text-body">Delivery fee refund</span>
                  <span className="text-heading">${calculateRefund.delivery.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-medium">
                <span className="text-heading">Total refund</span>
                <span className="text-success text-lg">${calculateRefund.total.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Impact Warning */}
            <div className="mt-4 bg-warning/10 text-warning rounded-lg p-3 text-sm flex items-start space-x-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                This refund will be processed via Stripe. 
                Business payout will be reduced by ${calculateRefund.total.toFixed(2)}.
              </span>
            </div>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="border-t border-gray-100 bg-gray-50 flex justify-end space-x-3">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!hasSelection || !reason.trim()}
          loading={isSubmitting}
          className="bg-success hover:bg-green-600"
        >
          Process Refund (${calculateRefund.total.toFixed(2)})
        </Button>
      </CardFooter>
    </Card>
  );
}


